import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import { useAuthStore } from '@/store/auth';
import axios from 'axios';
import useNotifications from '@/store/notification.js';
import { useRoute } from 'vue-router';
import Cookies from 'js-cookie';
import { useLogger } from '@/composables/useLogger';

const logger = useLogger('cart');

const openIndexedDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('CartDB', 1);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains('cart')) {
        db.createObjectStore('cart', { keyPath: 'id', autoIncrement: true });
      }
    };

    request.onsuccess = (event) => resolve(event.target.result);
    request.onerror = (event) => reject(event.target.error);
  });
};

const getIndexedDBData = async () => {
  const db = await openIndexedDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('cart', 'readonly');
    const store = transaction.objectStore('cart');
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result);
    request.onerror = (event) => reject(event.target.error);
  });
};

const saveIndexedDBData = async (data) => {
  const db = await openIndexedDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('cart', 'readwrite');
    const store = transaction.objectStore('cart');
    store.clear(); // Clear existing data

    // Convert all items to plain objects
    data.forEach((item) => store.add(JSON.parse(JSON.stringify(item))));

    transaction.oncomplete = () => resolve();
    transaction.onerror = (event) => reject(event.target.error);
  });
};

const clearIndexedDBData = async () => {
  const db = await openIndexedDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('cart', 'readwrite');
    const store = transaction.objectStore('cart');
    const request = store.clear();

    request.onsuccess = () => resolve();
    request.onerror = (event) => reject(event.target.error);
  });
};

// Helper to generate a unique ID for guest cart items
const makeGuestCartItemId = () => {
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

// Normalize any date-like value to 'YYYY-MM-DD' if possible
const normalizeDateString = (value) => {
  if (!value) return null;
  if (typeof value === 'string' && value.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  const d = new Date(value);
  if (isNaN(d)) return value; // fallback: return as-is if it's weird
  return d.toISOString().slice(0, 10);
};

// Helper to build a unique line key from equipment + dates
const buildLineKey = (equipmentId, start_date, end_date) => {
  const normStart = normalizeDateString(start_date);
  const normEnd = normalizeDateString(end_date);
  return `${String(equipmentId)}::${normStart}::${normEnd}`;
};

// Helper: generate array of date strings YYYY-MM-DD from start to end (inclusive)
const getDateRangeInclusive = (startStr, endStr) => {
  const dates = [];
  if (!startStr || !endStr) return dates;

  const start = new Date(startStr);
  const end = new Date(endStr);

  if (isNaN(start) || isNaN(end)) return dates;

  let cur = new Date(start);
  while (cur <= end) {
    dates.push(cur.toISOString().slice(0, 10));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
};

// Safely extract a stable equipment_id from a cart item
const getEquipmentIdFromCartItem = (ci) => {
  if (!ci) return null;
  if (ci.equipment_id != null) return String(ci.equipment_id);

  if (ci.item_details && ci.item_details.id != null) {
    return String(ci.item_details.id);
  }
  if (ci.item && typeof ci.item === 'object' && ci.item.id != null) {
    return String(ci.item.id);
  }
  if (ci.item != null && (typeof ci.item === 'string' || typeof ci.item === 'number')) {
    return String(ci.item);
  }

  return null;
};

export const useCartStore = defineStore('cart', () => {
  const api_base_url = import.meta.env.VITE_API_BASE_URL;

  const cart = ref([]); // Array to hold cart items
  const authStore = useAuthStore();
  const route = useRoute();
  const { showNotification } = useNotifications(); // Initialize notification service

  /**
   * RBAC guard: returns true if the authenticated user may perform cart operations.
   * Lessors cannot rent — they list. Guests (unauthenticated) can use the local cart.
   */
  const _assertCanUseCart = () => {
    if (!authStore.isAuthenticated) return true; // guest → local IndexedDB cart is fine
    const role = authStore.user?.role;
    const isAdmin = !!authStore.user?.is_staff;
    if (role === 'lessee' || isAdmin) return true;
    showNotification(
      'Not permitted',
      'Lessor accounts cannot add items to a cart. Switch to a lessee account to rent equipment.',
      'error',
    );
    return false;
  };

  // Helper: always get the equipment object for a cart item
  // - Authenticated: item_details (full Equipment)
  // - Guest: item (full Equipment), or fallback to cartItem itself
  const getEquipmentFromCartItem = (cartItem) => {
    if (!cartItem) return null;

    if (cartItem.item_details) {
      return cartItem.item_details;
    }

    if (cartItem.item && typeof cartItem.item === 'object') {
      return cartItem.item;
    }

    // Fallback: if the cart item itself has name/hourly_rate, use it
    return cartItem;
  };

  // Automatically calculated totals based on cart items (fallback if total missing)
  const cartTotalPrice = computed(() =>
    cart.value.reduce(
      (total, item) => total + (parseFloat(item.total) || 0),
      0
    )
  );

  const totalCartItems = computed(() => cart.value.length);

  // Load cart based on user's authentication status
  const loadCart = async () => {
    // Lessors have no cart — short-circuit before any backend call
    if (authStore.isAuthenticated) {
      const role = authStore.user?.role;
      const isAdmin = !!authStore.user?.is_staff;
      if (role !== 'lessee' && !isAdmin) {
        cart.value = [];
        return;
      }
    }

    if (authStore.isAuthenticated) {
      // Logged-in: load from backend
      try {
        const response = await axios.get(`${api_base_url}/api/cart-items/`, {
          withCredentials: true,
        });

        // Normalise equipment_id & dates for authenticated cart items too
        cart.value = (response.data || []).map((ci) => {
          const equipment_id = getEquipmentIdFromCartItem(ci);
          return {
            ...ci,
            equipment_id,
            start_date: normalizeDateString(ci.start_date),
            end_date: normalizeDateString(ci.end_date),
            lineKey: buildLineKey(equipment_id, ci.start_date, ci.end_date),
          };
        });
      } catch (error) {
        showNotification('Error', 'Could not load your cart items.', 'error');
      }
    } else {
      // Guest: load from IndexedDB and HYDRATE equipment details if needed
      try {
        const dbData = await getIndexedDBData();

        const hydrated = await Promise.all(
          (dbData || []).map(async (ci) => {
            let updated = { ...ci };

            // Normalise date fields
            updated.start_date = normalizeDateString(updated.start_date);
            updated.end_date = normalizeDateString(updated.end_date);

            // Ensure equipment_id is set
            updated.equipment_id = getEquipmentIdFromCartItem(updated);

            // Case 1: already has full equipment object → keep it
            if (updated.item && typeof updated.item === 'object') {
              updated.lineKey = buildLineKey(
                updated.equipment_id,
                updated.start_date,
                updated.end_date
              );
              return updated;
            }

            // Case 2: item is an ID → fetch equipment details
            if (updated.equipment_id && (!updated.item || typeof updated.item !== 'object')) {
              try {
                const res = await axios.get(
                  `${api_base_url}/api/equipments/${updated.equipment_id}/`
                );
                const equipment = res.data;

                updated.item = equipment;
                updated.equipment_id = String(equipment.id);
                updated.lineKey = buildLineKey(
                  updated.equipment_id,
                  updated.start_date,
                  updated.end_date
                );
                return updated;
              } catch (e) {
                logger.error('Failed to hydrate equipment for cart item', { equipment_id: updated.equipment_id, error: e });
                // fallback: just return the updated base
                updated.lineKey = buildLineKey(
                  updated.equipment_id,
                  updated.start_date,
                  updated.end_date
                );
                return updated;
              }
            }

            // Case 3: no item at all – just ensure keys
            updated.lineKey = buildLineKey(
              updated.equipment_id,
              updated.start_date,
              updated.end_date
            );
            return updated;
          })
        );

        cart.value = hydrated;
      } catch (error) {
        cart.value = [];
      }
    }
  };

  // Watch the authentication state to load cart data accordingly
  watch(
    () => authStore.isAuthenticated,
    async () => {
      await loadCart();
    },
    { immediate: true }
  );

  // Show notification if item not found
  const notifyItemNotFound = () => {
    showNotification('Error', 'Item not found in cart.', 'error');
  };

  // Remove item from the cart (local only; API delete is handled in the component)
  const removeItem = async (itemId) => {
    const index = cart.value.findIndex((item) => item.id === itemId);
    if (index !== -1) {
      cart.value.splice(index, 1);
      // Only persist to IndexedDB for guests
      if (!authStore.isAuthenticated) {
        await saveIndexedDBData(cart.value);
      }
    } else {
      notifyItemNotFound();
    }
  };

  /**
   * Check overlap capacity for GUEST cart:
   * For each day in [start_date, end_date], sum up quantities for this equipment
   * across existing lines (optionally excluding one line), then add `newQuantity`.
   * If any day exceeds maxQty, return { ok: false, day, allowed }.
   *
   * IMPORTANT: when excludeItemId is provided, newQuantity is treated as the
   * FINAL desired quantity for that excluded line.
   */
  const canAddBookingForGuest = (
    equipmentId,
    start_date,
    end_date,
    newQuantity,
    maxQty,
    excludeItemId = null
  ) => {
    const equipmentKey = String(equipmentId);
    const normStart = normalizeDateString(start_date);
    const normEnd = normalizeDateString(end_date);
    const dateRange = getDateRangeInclusive(normStart, normEnd);

    for (const day of dateRange) {
      let existingQty = 0;

      cart.value.forEach((ci) => {
        if (excludeItemId && ci.id === excludeItemId) return;

        const ciEquipKey = getEquipmentIdFromCartItem(ci);
        if (!ciEquipKey || ciEquipKey !== equipmentKey) return;

        const ciStart = normalizeDateString(ci.start_date);
        const ciEnd = normalizeDateString(ci.end_date);

        // check if this cart line covers `day` (inclusive)
        if (ciStart <= day && ciEnd >= day) {
          existingQty += parseInt(ci.quantity, 10) || 0;
        }
      });

      const newTotal = existingQty + newQuantity;
      if (newTotal > maxQty) {
        const allowed = maxQty - existingQty;
        return {
          ok: false,
          day,
          allowed: allowed < 0 ? 0 : allowed,
        };
      }
    }

    return { ok: true };
  };

  // Add item to cart:
  // - Authenticated -> backend handles everything.
  // - Guest:
  //    • same equipment + SAME dates => KEEP ONE ROW, ADD to existing quantity
  //    • same equipment + DIFFERENT dates => ALWAYS NEW ROW
  //      with strict overlap/availability checking
  const addItemToCart = async ({ equipment, quantity, start_date, end_date }) => {
    if (!_assertCanUseCart()) return;

    const isAuthenticated = authStore.isAuthenticated;

    // Basic validation
    if (!equipment || equipment.id == null) {
      showNotification('Error', 'Invalid equipment.', 'error');
      return;
    }

    if (!start_date || !end_date) {
      showNotification('Error', 'Please select start and end dates.', 'error');
      return;
    }

    quantity = parseInt(quantity, 10);
    if (isNaN(quantity) || quantity <= 0) {
      showNotification('Invalid Quantity', 'Please enter a valid quantity.', 'error');
      return;
    }

    const maxQty =
      typeof equipment.available_quantity === 'number'
        ? equipment.available_quantity
        : Infinity;

    if (quantity > maxQty) {
      showNotification(
        'Quantity Exceeds Availability',
        `Only ${maxQty} items are available.`,
        'error'
      );
      return;
    }

    // ============================  
    // AUTHENTICATED FLOW  
    // ============================  
    if (isAuthenticated) {
      try {
        const payload = {
          item: equipment.id,
          quantity,
          start_date,
          end_date,
        };

        await axios.post(`${api_base_url}/api/cart-items/`, payload, {
          withCredentials: true,
        });

        showNotification(
          'Added to Cart',
          `${equipment.name} has been added to your cart.`,
          'success'
        );

        // Reload cart from backend so we get server-side behavior
        await loadCart();
      } catch (error) {
        const backendMsg =
          error?.response?.data?.error ||
          error?.response?.data?.detail ||
          'Could not add item to cart.';
        showNotification('Error', backendMsg, 'error');
      }

      return;
    }

    // ============================  
    // GUEST FLOW (IndexedDB)  
    // ============================  

    const equipmentKey = String(equipment.id);
    const normStart = normalizeDateString(start_date);
    const normEnd = normalizeDateString(end_date);

    // 1. Find existing line with same equipment + same dates
    const sameRangeLine = cart.value.find((ci) => {
      const ciEquipKey = getEquipmentIdFromCartItem(ci);
      if (!ciEquipKey || ciEquipKey !== equipmentKey) return false;

      const ciStart = normalizeDateString(ci.start_date);
      const ciEnd = normalizeDateString(ci.end_date);

      return ciStart === normStart && ciEnd === normEnd;
    });

    if (sameRangeLine) {
      // ============================  
      // SAME ITEM + SAME DATES  
      // → KEEP ONE ROW, ADD TO EXISTING QUANTITY  
      // ============================  

      const currentQty = parseInt(sameRangeLine.quantity, 10) || 0;
      const finalQuantity = currentQty + quantity;

      const overlapCheckMerge = canAddBookingForGuest(
        equipment.id,
        normStart,
        normEnd,
        finalQuantity,     // FINAL desired quantity for this row
        maxQty,
        sameRangeLine.id   // exclude THIS row's current qty from base
      );

      if (!overlapCheckMerge.ok) {
        const maxFinal = overlapCheckMerge.allowed;
        const additionalAllowed = Math.max(maxFinal - currentQty, 0);

        return showNotification(
          'Not Enough Stock',
          additionalAllowed > 0
            ? `You can only add ${additionalAllowed} more units for these dates (maximum ${maxFinal} in total).`
            : `You have already reached the maximum available quantity for these dates.`,
          'error'
        );
      }

      sameRangeLine.quantity = finalQuantity;

      const price =
        typeof equipment.hourly_rate === 'number'
          ? equipment.hourly_rate
          : parseFloat(equipment.hourly_rate || 0);

      sameRangeLine.total = price * finalQuantity;
      sameRangeLine.equipment_id = equipmentKey;
      sameRangeLine.start_date = normStart;
      sameRangeLine.end_date = normEnd;
      sameRangeLine.lineKey = buildLineKey(equipmentKey, normStart, normEnd);

      showNotification(
        'Cart Updated',
        `${equipment.name} quantity has been updated to ${finalQuantity} for ${normStart} to ${normEnd}.`,
        'success'
      );
    } else {
      // ============================  
      // SAME ITEM + DIFFERENT DATES  
      // → ALWAYS NEW ROW  
      // ============================  

      const overlapCheckNew = canAddBookingForGuest(
        equipment.id,
        normStart,
        normEnd,
        quantity,
        maxQty,
        null
      );

      if (!overlapCheckNew.ok) {
        return showNotification(
          'Not Enough Stock',
          `Only ${overlapCheckNew.allowed} units are available on ${overlapCheckNew.day}. Please reduce quantity or change dates.`,
          'error'
        );
      }

      const price =
        typeof equipment.hourly_rate === 'number'
          ? equipment.hourly_rate
          : parseFloat(equipment.hourly_rate || 0);

      const newCartItem = {
        id: makeGuestCartItemId(), // unique local ID
        item: equipment,           // full equipment for guests
        equipment_id: equipmentKey,
        quantity,
        start_date: normStart,
        end_date: normEnd,
        ordered: false,
        total: price * quantity,
        lineKey: buildLineKey(equipmentKey, normStart, normEnd),
      };

      cart.value.push(newCartItem);

      showNotification(
        'Added to Cart',
        `${equipment.name} has been added to your cart for ${normStart} to ${normEnd}.`,
        'success'
      );
    }

    // Persist to IndexedDB for guests
    await saveIndexedDBData(cart.value);
  };

  // Update item quantity in cart
  // For guests we also enforce overlap capacity inside cart.
  const updateItemQuantity = async (itemId, quantity) => {
    if (!_assertCanUseCart()) return;

    const isAuthenticated = authStore.isAuthenticated;
    const foundItem = cart.value.find((item) => item.id === itemId);

    if (!foundItem) {
      showNotification('Error', 'Item not found in the cart.', 'error');
      return;
    }

    quantity = parseInt(quantity, 10);
    if (isNaN(quantity) || quantity <= 0) {
      showNotification('Invalid Quantity', 'Please enter a valid quantity.', 'error');
      return;
    }

    const equipment = getEquipmentFromCartItem(foundItem);
    const maxQty =
      equipment && typeof equipment.available_quantity === 'number'
        ? equipment.available_quantity
        : Infinity;

    // Extra guest overlap check (new quantity is absolute)
    if (!isAuthenticated) {
      const normStart = normalizeDateString(foundItem.start_date);
      const normEnd = normalizeDateString(foundItem.end_date);
      const overlapCheck = canAddBookingForGuest(
        equipment.id,
        normStart,
        normEnd,
        quantity,        // new desired quantity
        maxQty,
        foundItem.id     // exclude this line's current qty from base
      );

      if (!overlapCheck.ok) {
        return showNotification(
          'Not Enough Stock',
          `Only ${overlapCheck.allowed} units are available on ${overlapCheck.day} for those dates.`,
          'error'
        );
      }
    }

    foundItem.quantity = quantity;

    const price =
      equipment && typeof equipment.hourly_rate === 'number'
        ? equipment.hourly_rate
        : parseFloat(equipment?.hourly_rate || 0);
    foundItem.total = price * quantity;

    const name = equipment?.name || 'Item';

    showNotification('Quantity Updated', `${name} quantity has been updated.`, 'success');

    if (!isAuthenticated) {
      await saveIndexedDBData(cart.value);
    }
  };

  const syncGuestCartToBackend = async () => {
  // Only sync if user is logged in
  if (!authStore.isAuthenticated) return;

  // 1. Read guest cart from IndexedDB
  const guestItems = await getIndexedDBData();
  if (!guestItems || guestItems.length === 0) {
    await loadCart(); // nothing to sync, just load server cart
    return;
  }

  // 2. Push each guest item to backend
  for (const ci of guestItems) {
    const equipment = ci.item || ci.item_details;
    if (!equipment || equipment.id == null) continue;

    const payload = {
      item: equipment.id,
      quantity: parseInt(ci.quantity, 10) || 1,
      start_date: ci.start_date,
      end_date: ci.end_date,
    };

    try {
      await axios.post(`${api_base_url}/api/cart-items/`, payload, {
        withCredentials: true,
      });
      // Backend CartItemViewSet.create does:
      // - owner / lessor checks
      // - overlap/per-day availability
      // - SAME dates → merge line
      // - DIFFERENT dates → new line
    } catch (error) {
      logger.error('Failed to sync guest cart item', { payload, error });
      // Optional: showNotification once or per item
    }
  }

  // 3. Clear guest storage and load final server cart
  await clearIndexedDBData();
  await loadCart();
};


  // Clear the cart
  const clearCart = async () => {
    cart.value = [];
    await clearIndexedDBData();
  };

  const currentCategory = computed(() => route.params.cat || ''); // Retrieve category from URL

  return {
    api_base_url,
    cart,
    currentCategory,
    cartTotalPrice,
    totalCartItems,
    addItemToCart,
    removeItem,
    updateItemQuantity,
    clearCart,
    loadCart,
    getIndexedDBData,
    saveIndexedDBData,
    syncGuestCartToBackend
  };
});
